import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    FormControl,
    Select,
    MenuItem,
    OutlinedInput,
    Radio,
    ListItemText,
    ListSubheader,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import styles from './FilterSelect.module.css';

const FilterSelect = ({ label, options = [], value, onChange, compact = false, disabled = false }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    const filteredOptions = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return options;
        return options.filter((option) => String(option).toLowerCase().includes(query));
    }, [options, search]);

    const handleSelect = (selectedValue) => {
        onChange(label, selectedValue);
        setOpen(false);
    };

    const handleClose = () => {
        setOpen(false);
        setSearch('');
    };

    const menuProps = {
        autoFocus: false,
        slotProps: {
            paper: {
                sx: {
                    marginTop: 1,
                    borderRadius: '10px',
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                },
            },
        },
    };

    const menuItems = (
        <>
            <ListSubheader
                sx={{ padding: '8px 8px 6px', lineHeight: 'normal' }}
                onClick={(event) => event.stopPropagation()}
            >
                <TextField
                    size="small"
                    fullWidth
                    autoFocus
                    placeholder="Search..."
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key !== 'Escape') event.stopPropagation();
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 18, color: '#98A7B4' }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            fontSize: 13,
                            backgroundColor: '#F8FBFC',
                        },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D9E1E7' },
                    }}
                />
            </ListSubheader>
            <MenuItem
                onClick={() => handleSelect('')}
                sx={{
                    padding: '8px 16px',
                    '& .MuiListItemText-root span': { fontSize: 14, color: '#1f1f1f' },
                }}
            >
                <Radio
                    checked={!value}
                    size="small"
                    tabIndex={-1}
                    sx={{ pointerEvents: 'none', color: '#D9E1E7', '&.Mui-checked': { color: '#1f36c7' } }}
                />
                <ListItemText primary="All" />
            </MenuItem>
            {options.length > 0 && filteredOptions.length === 0 ? (
                <MenuItem disabled sx={{ justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 13, color: '#98A7B4' }}>No results found</Typography>
                </MenuItem>
            ) : (
                filteredOptions.map((option) => (
                    <MenuItem
                        key={option}
                        onClick={() => handleSelect(option)}
                        sx={{
                            padding: '8px 16px',
                            '& .MuiListItemText-root span': { fontSize: 14, color: '#1f1f1f' },
                        }}
                    >
                        <Radio
                            checked={value === option}
                            size="small"
                            tabIndex={-1}
                            sx={{ pointerEvents: 'none', color: '#D9E1E7', '&.Mui-checked': { color: '#1f36c7' } }}
                        />
                        <ListItemText primary={option} />
                    </MenuItem>
                ))
            )}
        </>
    );

    // Compact inline variant: "Label: Value ▾" on a single row, no separate label above
    if (compact) {
        const displayValue = value || 'All';
        return (
            <FormControl size="small" sx={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                <Select
                    open={open}
                    onOpen={() => setOpen(true)}
                    onClose={handleClose}
                    value={value || ''}
                    displayEmpty
                    disabled={disabled}
                    input={<OutlinedInput />}
                    renderValue={() => (
                        <span style={{ fontSize: 13, color: '#1A2025' }}>
                            <span style={{ color: '#667F93', fontWeight: 400 }}>{label}: </span>
                            <span style={{ fontWeight: 500 }}>{displayValue}</span>
                        </span>
                    )}
                    sx={{
                        height: 32,
                        backgroundColor: '#FFFFFF',
                        borderRadius: '8px',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#D9E1E7',
                            borderRadius: '8px',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1f36c7' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#1f36c7',
                            borderWidth: '1px',
                        },
                        '& .MuiSelect-select': { padding: '4px 10px' },
                    }}
                    MenuProps={menuProps}
                >
                    {menuItems}
                </Select>
            </FormControl>
        );
    }

    // Default full variant: label above + full-width select
    return (
        <div className={styles.container} style={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
            <Typography
                className={styles.label}
                sx={{
                    fontSize: 12,
                    color: '#667F93',
                    fontWeight: 400,
                    marginLeft: '4px',
                }}
            >
                {label}
            </Typography>
            <FormControl fullWidth>
                <Select
                    open={open}
                    onOpen={() => setOpen(true)}
                    onClose={handleClose}
                    value={value || ''}
                    displayEmpty
                    disabled={disabled}
                    input={<OutlinedInput />}
                    renderValue={(selected) => {
                        if (!selected) {
                            return <span style={{ color: '#9AA0A6' }}>All</span>;
                        }
                        return <span style={{ color: '#1A2025' }}>{selected}</span>;
                    }}
                    sx={{
                        height: 40,
                        backgroundColor: '#FFFFFF',
                        borderRadius: '10px',
                        fontSize: 14,
                        color: '#1A2025',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#D9E1E7',
                            borderRadius: '10px',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1f36c7' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#1f36c7',
                            borderWidth: '1px',
                        },
                        '& .MuiSelect-select': { color: '#1A2025', padding: '8px 14px' },
                    }}
                    slotProps={{
                        input: { sx: { color: '#1A2025', fontSize: 14 } },
                    }}
                    MenuProps={menuProps}
                >
                    {menuItems}
                </Select>
            </FormControl>
        </div>
    );
};

FilterSelect.propTypes = {
    label: PropTypes.string,
    options: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
    compact: PropTypes.bool,
    disabled: PropTypes.bool,
};

export default FilterSelect;
